import { useUnsavedChanges } from "../../layout/AppShellContext";
import React, { useRef, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import "./ticketChat.scss";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { api, getActiveCompanyId } from "../../api/client";
import { openApiEventStream } from "../../api/sse";
import { MessageAttachments } from "../shared/MessageAttachments";
import { SafeMessageBody } from "../shared/SafeMessageBody";
import { AssistantReplyFeedback, AssistantReply } from "../shared/AssistantReplyFeedback";
import { uploadFilesToApi } from "../shared/uploadFiles";
import { useTicketViewport } from "../shared/useTicketViewport";
import { useTicketScroll } from "../shared/useTicketScroll";

type ApiMessage = {
    id: number;
    body: string | null;
    files: any[] | null;
    dateCreate: string | null; // ISO
    dateUpdate: string | null; // ISO
    author: { id: number; name: string | null; email: string | null } | null;
    isMine?: boolean | null;
    aiReply?: AssistantReply;
};

type RatingState = {
    requested?: boolean;
    canRate?: boolean;
    score?: number | null;
    comment?: string | null;
    status?: string | null;
    submittedAt?: string | null;
};

const RATING_COMMENT_LIMIT = 1000;

type ApiMessagesResponse = {
    data: ApiMessage[];
    meta: { taskId: number; page: number; limit: number; total: number; order: "asc" | "desc"; rating?: RatingState | null };
};

type ApiPostMessageResponse = {
    data: ApiMessage;
    meta?: any;
};

type ApiRatingResponse = {
    data?: {
        rating?: RatingState | null;
    };
    rating?: RatingState | null;
    meta?: any;
};

type ApiMessageStreamEvent = {
    messages?: ApiMessage[];
    cursor?: {
        lastMessageId?: number;
        serverTime?: string;
    };
};

function sortMessages(items: ApiMessage[]): ApiMessage[] {
    return items
        .slice()
        .sort((left, right) => Number(left.id || 0) - Number(right.id || 0));
}

function mergeMessages(previous: ApiMessage[], incoming: ApiMessage[]): ApiMessage[] {
    if (!incoming.length) {
        return previous;
    }

    const map = new Map<number, ApiMessage>();
    previous.forEach((message) => map.set(message.id, message));
    incoming.forEach((message) => map.set(message.id, message));

    return sortMessages(Array.from(map.values()));
}

function resizeTextarea(textarea: HTMLTextAreaElement | null): void {
    if (!textarea) {
        return;
    }

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 92)}px`;
}

function fmt(dt: string | null): string {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d.getTime())) return dt ?? "";
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
        d.getMinutes()
    )}`;
}

function extractRating(payload: any): RatingState | null {
    return payload?.meta?.rating ?? payload?.data?.meta?.rating ?? payload?.data?.rating ?? payload?.rating ?? null;
}

function TicketRatingRequest({
    rating,
    submitting,
    onSubmit,
	}: {
	    rating: RatingState | null;
	    submitting: boolean;
	    onSubmit: (score: number, comment: string) => void;
	}) {
	    const score = Number(rating?.score || 0);
	    const existingComment = typeof rating?.comment === "string" ? rating.comment : "";
	    const [selectedScore, setSelectedScore] = useState(score > 0 ? score : 0);
	    const [comment, setComment] = useState(existingComment);
	
	    useEffect(() => {
	        setSelectedScore(score > 0 ? score : 0);
	        setComment(existingComment);
	    }, [score, existingComment]);
	
	    if (!rating?.requested) {
	        return null;
	    }
	
	    return (
	        <div className="ticket-chat-rating">
	            <div className="ticket-chat-rating-title">
	                <span>Оценка закрытия</span>
	                <strong>{score > 0 ? `${score}/5` : "Ожидает оценки"}</strong>
	            </div>
	            {rating.canRate ? (
	                <>
	                    <div className="ticket-chat-rating-buttons">
	                        {[1, 2, 3, 4, 5].map((value) => (
	                            <button
	                                key={value}
	                                type="button"
	                                className={selectedScore === value ? "active" : ""}
	                                disabled={submitting}
	                                onClick={() => setSelectedScore(value)}
	                            >
	                                {value}
	                            </button>
	                        ))}
	                    </div>
	                    <textarea
	                        className="ticket-chat-rating-comment"
	                        value={comment}
	                        maxLength={RATING_COMMENT_LIMIT}
	                        disabled={submitting}
	                        onChange={(event) => setComment(event.target.value)}
	                        placeholder="Комментарий к оценке"
	                    />
	                    <div className="ticket-chat-rating-footer">
	                        <span>{comment.length}/{RATING_COMMENT_LIMIT}</span>
	                        <button
	                            type="button"
	                            className="ticket-chat-rating-submit"
	                            disabled={submitting || selectedScore < 1}
	                            onClick={() => onSubmit(selectedScore, comment)}
	                        >
	                            {submitting ? "Сохраняем..." : "Сохранить оценку"}
	                        </button>
	                    </div>
	                </>
	            ) : (
	                <>
	                    <span className="ticket-chat-rating-note">
	                        {score > 0 ? "Оценка сохранена" : "Ожидаем оценку постановщика"}
	                    </span>
	                    {existingComment.trim() !== "" ? (
	                        <p className="ticket-chat-rating-readonly">{existingComment.trim()}</p>
	                    ) : null}
	                </>
	            )}
	        </div>
	    );
	}

export const TicketChatPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const taskId = Number(id);
    const [menuOpen, setMenuOpen] = useState(false);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<ApiMessage[]>([]);
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    useUnsavedChanges(!!input.trim() || files.length > 0 || sending, "Ответ в обращении");
    const [ratingSubmitting, setRatingSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rating, setRating] = useState<RatingState | null>(null);
    const [page] = useState(1);
    const [limit] = useState(50);
    const [streamReady, setStreamReady] = useState(false);

    const generation = useRef(0);
    useEffect(() => {
        const counter = generation; ++counter.current;
        setInput(""); setFiles([]); setMessages([]); setRating(null); setSending(false); setRatingSubmitting(false);
        return () => { ++counter.current; };
    }, [taskId]);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const pageRef = useRef<HTMLDivElement>(null);
    const lastMessageIdRef = useRef(0);

    useTicketViewport(pageRef);
    const { messagesRef, onMessagesScroll, scrollToLatest, hasNewMessages } =
        useTicketScroll(taskId, messages.length, loading);

    useEffect(() => {
        resizeTextarea(inputRef.current);
    }, [input]);

    useEffect(() => {
        if (!Number.isFinite(taskId)) return;
        let cancel = false;
        setLoading(true);
        setError(null);
        setStreamReady(false);
        lastMessageIdRef.current = 0;
        api
            .get<ApiMessagesResponse>(`/api/gateway/tasks/${taskId}/messages`, { page, limit, order: "desc" })
            .then((res) => {
                if (cancel) return;
                const nextMessages = sortMessages(Array.isArray(res?.data) ? res.data : []);
                lastMessageIdRef.current = nextMessages[nextMessages.length - 1]?.id ?? 0;
                setMessages(nextMessages);
                setRating(extractRating(res));
                setStreamReady(true);
            })
            .catch((e) => {
                if (cancel) return;
                setError(e?.message || "Ошибка загрузки");
            })
            .finally(() => {
                if (!cancel) setLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [taskId, page, limit]);

    useEffect(() => {
        if (!Number.isFinite(taskId) || !streamReady) {
            return;
        }

        return openApiEventStream(
            () => `/api/gateway/tasks/${taskId}/messages/stream?lastMessageId=${lastMessageIdRef.current}`,
            {
                onEvent: (event) => {
                    if (event.event !== "task_messages") {
                        return;
                    }

                    try {
                        const payload = JSON.parse(event.data) as ApiMessageStreamEvent;
                        const incoming = sortMessages(Array.isArray(payload.messages) ? payload.messages : []);
                        if (!incoming.length) {
                            return;
                        }

                        lastMessageIdRef.current = Math.max(
                            lastMessageIdRef.current,
                            payload.cursor?.lastMessageId ?? incoming[incoming.length - 1]?.id ?? 0
                        );
                        setMessages((previous) => mergeMessages(previous, incoming));
                    } catch {
                        // ignore malformed payloads
                    }
                },
                onError: () => {
                    // reconnect is handled inside the helper
                },
            }
        );
    }, [streamReady, taskId]);


    const title = useMemo(() => `Задача #${taskId}`, [taskId]);

    const handlePickFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(event.target.files ?? []);
        if (picked.length > 0) {
            setFiles((current) => [...current, ...picked]);
        }
        event.target.value = "";
    };

    const removeFile = (index: number) => {
        setFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
    };

    const sendMessage = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const epoch = generation.current; const selectedCompany = getActiveCompanyId();
        const raw = input.trim();
        const selectedFiles = files;
        if ((!raw && selectedFiles.length === 0) || !Number.isFinite(taskId) || sending) return;

        try {
            setSending(true);
            setError(null);

            const uploadedFiles = selectedFiles.length > 0 ? await uploadFilesToApi(selectedFiles) : [];
            if (epoch !== generation.current) return;
            const res = await api.post<ApiPostMessageResponse>(`/api/gateway/tasks/${taskId}/messages`, {
                body: raw,
                ...(uploadedFiles.length > 0 ? { files: uploadedFiles } : {}),
            }, { headers: { "X-Company-Id": selectedCompany || "" } });
            if (epoch !== generation.current) return;

            const created = res?.data;
            if (created) {
                lastMessageIdRef.current = Math.max(lastMessageIdRef.current, created.id ?? 0);
                setMessages((prev) => mergeMessages(prev, [created]));
                setInput("");
                setFiles([]);
                scrollToLatest();
            }
        } catch (err: any) {
            if (epoch !== generation.current) return;
            setError(err?.message || "Ошибка отправки");
        } finally {
            if (epoch === generation.current) setSending(false);
        }
    };

	    const submitRating = async (score: number, comment: string) => {
	        if (!Number.isFinite(taskId) || ratingSubmitting) return;
	
	        try {
	            setRatingSubmitting(true);
	            setError(null);
	            const response = await api.post<ApiRatingResponse>(`/api/gateway/tasks/${taskId}/rating`, { score, comment });
	            const nextRating = extractRating(response);
	            if (nextRating) {
	                setRating(nextRating);
            }
        } catch (err: any) {
            setError(err?.message || "Ошибка сохранения оценки");
        } finally {
            setRatingSubmitting(false);
        }
    };

    return (
        <div className="ticket-chat-page" ref={pageRef}>
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
            <div className="ticket-chat-header">
                <MenuHeader onMenuClick={() => setMenuOpen(true)} />
                <div className="ticket-chat-title-row">
                    <div className="ticket-chat-title">{title}</div>
                </div>
            </div>

            <TicketRatingRequest
                rating={rating}
                submitting={ratingSubmitting}
                onSubmit={submitRating}
            />

            <div className="ticket-chat-messages" ref={messagesRef} onScroll={onMessagesScroll}>
                {loading && <div className="ticket-chat-empty">Загрузка…</div>}
                {error && !loading && <div className="ticket-chat-empty error">Ошибка: {error}</div>}
                {!loading &&
                !error &&
                messages.map((msg) => {
                    const isMine = msg.isMine ?? !msg.author;
                    const hasFiles = Array.isArray(msg.files) && msg.files.length > 0;
                    const hasBody = Boolean((msg.body ?? "").trim());
                    return (
                        <div key={msg.id} className={`ticket-chat-message ${isMine ? "user" : "support"}`}>
                            {hasBody && (
                                <div className="message-bubble"><SafeMessageBody body={msg.body} /></div>
                            )}
                            {hasFiles && <MessageAttachments files={msg.files} />}
                            <AssistantReplyFeedback reply={msg.aiReply} taskId={String(taskId)} messageId={msg.id} />
                            <div className="message-date">{fmt(msg.dateCreate)}</div>
                        </div>
                    );
                })}
            </div>

            {hasNewMessages && (
                <button type="button" className="ticket-chat-new-messages" onClick={scrollToLatest}>
                    Новые сообщения ↓
                </button>
            )}

            <form className="ticket-chat-input-row" onSubmit={sendMessage}>
                {files.length > 0 && (
                    <div className="ticket-chat-selected-files">
                        {files.map((file, index) => (
                            <div className="ticket-chat-selected-file" key={`${file.name}-${file.size}-${index}`}>
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    disabled={sending}
                                    aria-label={`Удалить ${file.name}`}
                                >
                                    x
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="ticket-chat-compose-line">
                    <button
                        className="ticket-chat-attach-btn"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        aria-label="Добавить файл"
                    >
                        +
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handlePickFiles}
                    />
                    <textarea
                        ref={inputRef}
                        className="ticket-chat-input"
                        placeholder="Введите сообщение..."
                        value={input}
                        rows={1}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={sending}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                    />
                    <button
                        className="ticket-chat-send-btn"
                        type="submit"
                        disabled={(!input.trim() && files.length === 0) || sending}
                    >
                        {sending ? "..." : "Отправить"}
                    </button>
                </div>
            </form>
        </div>
    );
};
